import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import {
  type HandoverPage,
  type ListSubmissionsParams,
  type SubmissionPage,
  countSubmissions,
  getReview,
  listHandovers,
  listSubmissions,
} from "@/features/submissions/api";

/**
 * The queue. Keyset-paginated like every other list in this app, so an
 * infinite query and never a page number.
 *
 * Every filter is in the key, `scope` included — a key that omitted it
 * would show the previous tab's rows while the new request was in
 * flight, which reads as the tab not working.
 */
export function submissionsQuery(params: ListSubmissionsParams) {
  return infiniteQueryOptions({
    queryKey: ["submissions", params],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      listSubmissions({ ...params, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: SubmissionPage) => page.next_cursor ?? undefined,
  });
}

/**
 * One submission's review view.
 *
 * `staleTime: 0` and a refetch on focus, deliberately the opposite of
 * `recordEditQuery`: this page's whole content is "is this still waiting
 * on me", and coming back to a tab to find a submission somebody else
 * decided ten minutes ago is exactly when a reviewer needs to be told.
 * There is no form state here for a refetch to disturb.
 */
export function reviewQuery(submissionId: string) {
  return queryOptions({
    queryKey: ["submission-review", submissionId],
    queryFn: () => getReview(submissionId),
  });
}

/** The dashboard's approvals-summary tile — a pending count for one
 *  `kind`, scoped exactly the way the queue itself would resolve. */
export function submissionCountQuery(kind: "schema" | "record") {
  return queryOptions({
    queryKey: ["submission-count", { status: "pending", kind }],
    queryFn: () => countSubmissions({ status: "pending", kind }),
    staleTime: 30 * 1000,
  });
}

export function handoversQuery(params: { status?: string | undefined } = {}) {
  return infiniteQueryOptions({
    queryKey: ["handovers", params],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      listHandovers({ ...params, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: HandoverPage) => page.next_cursor ?? undefined,
  });
}
